/**
 * ATLAS TERMINAL — crypto/forex-style monitoring (READ-ONLY).
 *
 * Maps existing Atlas API (domain_coverage + insights + state + scoring)
 * to trading-platform grammar:
 *   price     -> severity / system strain
 *   volume    -> signal count
 *   watchlist -> active issues table
 *   movers    -> fastest-changing sectors (via spark history)
 *   order book-> evidence / source stack
 *   ticker    -> live system event feed
 *
 * No trading controls. No buy/sell. Monitoring only.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../lib/api';
import '../../styles/studio.css';
import './atlas-terminal.css';

import SectorHeatmapGrid from './SectorHeatmapGrid';
import RiskWatchlistTable from './RiskWatchlistTable';
import ActiveIssueBoard from './ActiveIssueBoard';
import AtlasTickerBar from './AtlasTickerBar';
import AtlasTerminalTopBar from './AtlasTerminalTopBar';
import LightweightChart from '../charts/LightweightChart';
import LiveTalkButton from '../voice/LiveTalkButton';
import AtlasMarketsTape from './AtlasMarketsTape';

export default function AtlasTerminal({ onSwitchToNova, onSwitchToOrion, onSwitchToVega, onSwitchToShow, onSwitchToCrew }) {
  const [worldState, setWorldState] = useState(null);
  const [selectedInsightId, setSelectedInsightId] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [history, setHistory] = useState({}); // sector -> number[] strain history

  const fetchState = useCallback(async () => {
    try {
      const res = await api.get('/api/atlas/state');
      setWorldState(res.data);
      // Auto-select the highest-severity insight on first load.
      const insights = res.data.insights || [];
      if (!selectedInsightId && insights.length > 0) {
        const top = insights.reduce((a, b) => ((a.severity || 0) > (b.severity || 0) ? a : b));
        setSelectedInsightId(top.id);
      }
      // Maintain per-sector strain history (bounded ring buffer) for mini-bars.
      const dc = res.data.state?.domain_coverage || {};
      setHistory((prev) => {
        const next = { ...prev };
        Object.entries(dc).forEach(([sector, cov]) => {
          const strain = typeof cov === 'number' ? cov : cov?.strain ?? cov?.value ?? 0;
          const series = (next[sector] || []).slice(-23);
          series.push(Number(strain) || 0);
          next[sector] = series;
        });
        return next;
      });
      setTick((t) => t + 1);
    } catch (err) {
      console.error('[Atlas] state fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [selectedInsightId]);

  useEffect(() => {
    fetchState();
    const id = setInterval(fetchState, 30000);
    return () => clearInterval(id);
  }, [fetchState]);

  const insights = worldState?.insights || [];
  const selectedInsight = useMemo(
    () => insights.find((i) => i.id === selectedInsightId) || insights[0] || null,
    [insights, selectedInsightId]
  );

  // Aggregate global-strain time series from every sector's bounded history —
  // feeds the bottom lightweight-charts strip ("system strain over session").
  const strainSeries = useMemo(() => {
    const entries = Object.values(history);
    if (entries.length === 0) return [];
    const longest = entries.reduce((m, s) => Math.max(m, s.length), 0);
    const nowSec = Math.floor(Date.now() / 1000);
    const stepSec = 30; // matches the 30 s poll cadence
    const series = [];
    for (let i = 0; i < longest; i += 1) {
      let sum = 0;
      let count = 0;
      entries.forEach((s) => {
        const v = s[i];
        if (typeof v === 'number') { sum += v; count += 1; }
      });
      if (count === 0) continue;
      series.push({
        time: nowSec - (longest - 1 - i) * stepSec,
        value: Number((sum / count).toFixed(3)),
      });
    }
    return series;
  }, [history]);

  if (loading) {
    return (
      <div className="atlas-terminal" data-testid="atlas-terminal-loading">
        <div style={{ gridColumn: '1 / -1', placeSelf: 'center', paddingTop: 120 }}>
          <div className="atl-load-ring" />
          <div className="studio-label" style={{ textAlign: 'center', color: 'var(--neon-atlas)', letterSpacing: '0.3em' }}>ATLAS TERMINAL</div>
          <div style={{ color: 'var(--studio-text-2)', fontSize: 12, textAlign: 'center', marginTop: 6 }}>Establishing uplink…</div>
        </div>
      </div>
    );
  }
  if (!worldState) {
    return (
      <div className="atlas-terminal" data-testid="atlas-terminal-error">
        <div className="atl-empty">LINK LOST — unable to reach Atlas brain</div>
      </div>
    );
  }

  return (
    <div className="atlas-terminal" data-testid="atlas-terminal">
      <AtlasTerminalTopBar
        state={worldState.state}
        scoring={worldState.scoring}
        signalCount={worldState.signal_count}
        totalInsights={worldState.total_insight_count}
        timestamp={worldState.timestamp}
        sensorReadiness={worldState.sensor_readiness}
        onSwitchToNova={onSwitchToNova}
        onSwitchToOrion={onSwitchToOrion}
        onSwitchToVega={onSwitchToVega}
        onSwitchToShow={onSwitchToShow}
        onSwitchToCrew={onSwitchToCrew}
        onRefresh={fetchState}
      />

      <div className="atl-body" data-testid="atlas-terminal-body">
        {/* LEFT — sector heatmap grid */}
        <div className="atl-pane" data-testid="atl-pane-left">
          <div className="atl-pane-head">
            <span className="t">Sectors</span>
            <span className="sub">{Object.keys(worldState.state?.domain_coverage || {}).length} domains</span>
          </div>
          <SectorHeatmapGrid
            domainCoverage={worldState.state?.domain_coverage || {}}
            history={history}
            insights={insights}
            selectedSector={selectedSector}
            onSelectSector={setSelectedSector}
          />
        </div>

        {/* CENTER — risk watchlist table */}
        <div className="atl-pane" data-testid="atl-pane-center">
          <div className="atl-pane-head">
            <span className="t">Priority Watchlist</span>
            <span className="sub">{insights.length} active · sorted by severity</span>
          </div>
          <RiskWatchlistTable
            insights={insights}
            selectedInsightId={selectedInsight?.id}
            selectedSector={selectedSector}
            onSelect={setSelectedInsightId}
          />
        </div>

        {/* RIGHT — active issue briefing */}
        <div className="atl-pane" data-testid="atl-pane-right">
          <div className="atl-pane-head">
            <span className="t">Active Briefing</span>
            <span className="sub">#{selectedInsight?.id?.slice(0, 8) || '—'}</span>
          </div>
          <ActiveIssueBoard insight={selectedInsight} />
        </div>
      </div>

      <div
        className="atl-markets-strip"
        data-testid="atlas-markets-strip"
        style={{
          padding: '10px 14px 6px',
          borderTop: '1px solid rgba(120, 150, 200, 0.15)',
          background: 'rgba(5, 7, 11, 0.6)',
        }}
      >
        <div
          className="studio-label"
          style={{
            fontSize: 10,
            letterSpacing: '0.24em',
            color: 'var(--studio-text-2, rgba(180,200,230,0.55))',
            marginBottom: 4,
          }}
        >
          MARKETS · CRYPTO · COMMODITIES · FX
        </div>
        <AtlasMarketsTape />
      </div>

      <AtlasTickerBar
        insights={insights}
        offlineNotices={worldState.offlineNotices || worldState.offline_notices || []}
        connectors={worldState.connectors || {}}
        tick={tick}
      />

      {strainSeries.length > 1 && (
        <div
          className="atl-chart-strip"
          data-testid="atlas-strain-chart-strip"
          style={{
            padding: '8px 14px 10px',
            borderTop: '1px solid rgba(120, 150, 200, 0.15)',
            background: 'rgba(5, 7, 11, 0.6)',
          }}
        >
          <div
            className="studio-label"
            style={{
              fontSize: 10,
              letterSpacing: '0.24em',
              color: 'var(--studio-text-2, rgba(180,200,230,0.55))',
              marginBottom: 4,
            }}
          >
            SYSTEM STRAIN · SESSION
          </div>
          <LightweightChart
            data={strainSeries}
            series="area"
            height={90}
            color="#4de2c0"
            testId="atlas-strain-chart"
          />
        </div>
      )}
      <LiveTalkButton agent="atlas" label="Talk to Atlas" />
    </div>
  );
}
